import { THEMES, DEFAULT_THEME, deriveTheme } from '@ego/domain';
import { listCustomThemes } from './customThemes';

/** Stored selection scheme shared with iOS: builtin name or "custom:<uuid>". */
export const CUSTOM_THEME_PREFIX = 'custom:';

/** Theme token → CSS custom property (the 13 vars from index.css `:root`). */
const TOKEN_TO_CSS_VAR = {
  bgBase: '--bg-base',
  bgSurface: '--bg-surface',
  bgElevated: '--bg-elevated',
  bgHover: '--bg-hover',
  textPrimary: '--text-primary',
  textMuted: '--text-muted',
  textDim: '--text-dim',
  accent: '--accent',
  borderAccent: '--border-accent',
  egoAccent: '--ego-accent',
  egoBorder: '--ego-border',
  borderSubtle: '--border-subtle',
  blue: '--blue',
};

function clearInlineTokens(root) {
  for (const cssVar of Object.values(TOKEN_TO_CSS_VAR)) {
    root.style.removeProperty(cssVar);
  }
}

/**
 * Applies and persists a theme selection: a builtin name ('dusk' / 'light' /
 * 'dawn') or "custom:<uuid>" referencing a saved custom theme. Custom themes
 * are re-derived fresh from their seeds and applied as inline CSS custom
 * properties; an unknown custom id falls back to dusk.
 */
export default function applyTheme(selection) {
  const root = document.documentElement;

  if (typeof selection === 'string' && selection.startsWith(CUSTOM_THEME_PREFIX)) {
    const id = selection.slice(CUSTOM_THEME_PREFIX.length);
    const entry = listCustomThemes().find((t) => t.id === id);
    if (!entry) {
      applyTheme(DEFAULT_THEME);
      return;
    }
    const theme = deriveTheme(entry.name, entry.bgHex, entry.accentHex, entry.textHex);
    root.removeAttribute('data-theme');
    for (const [token, cssVar] of Object.entries(TOKEN_TO_CSS_VAR)) {
      root.style.setProperty(cssVar, theme[token]);
    }
    localStorage.setItem('ego-theme', selection);
    return;
  }

  const theme = THEMES[selection] ? selection : DEFAULT_THEME;
  clearInlineTokens(root);
  if (theme === DEFAULT_THEME) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
  localStorage.setItem('ego-theme', theme);
}

export function getStoredTheme() {
  return localStorage.getItem('ego-theme') || DEFAULT_THEME;
}
